import { useCallback, useEffect, useRef, useState } from "react"
import { useWidget } from "../../../../providers/widget"
import { useExplorer } from "../../providers"
import { ExploreWidgetConfig } from "../../config"
import { useMobileResults } from "../mobileResults/context"

export type MapBoundsBox = { north: number; south: number; east: number; west: number }

/**
 * Shared search-as-move control state machine for BOTH map renderers (#852).
 * Owns three toolkit-ported behaviors in one coordinated machine:
 *
 * - Stale-closure fix: the renderers' debounced position handlers live inside a
 *   mount-once effect, so they must read search-active through `searchActiveRef`
 *   (the old code closed over the initial `state.mapSearchActive === false` and
 *   bounds updates never fired after activation).
 * - `activateSearchOnFirstGesture` (toolkit search-as-move-default): armed until
 *   the FIRST genuine user pan/pinch/zoom; never activates during programmatic
 *   framing (fitBounds), which would narrow the unfiltered total. While armed the
 *   search checkbox is concealed; it reveals checked after activation.
 * - `searchAreaButton` (toolkit map-controls): a one-shot "Search this area"
 *   pill shown after a genuine move while search-as-move is off. First-gesture
 *   arming takes precedence — the pill never shows while armed.
 *
 * Renderers wire: `onUserGesture` to real-user input events ONLY (dragstart,
 * container wheel, pinch touchmove, zoom buttons), `onMoveEnd` to their
 * debounced non-programmatic position-change handler, `onToggle` to the
 * checkbox, `onSearchAreaClick` to the pill.
 */
export function useMapSearchControls() {
  const { options } = useWidget<ExploreWidgetConfig>()
  const { state, setMapBounds, setMapSearchActive } = useExplorer()
  const mobileResults = useMobileResults()

  // Mobile results sheet (#856): search-as-move is SUSPENDED while the sheet
  // layout is active — the toolkit hides the control there (css:86-95) and
  // its band shim refuses to fight an active search. The sheet renders the
  // map without changing the visitor's `state.mapVisible`, and every bounds
  // consumer (grid, totals, price histogram) is gated on that flag, so a
  // machine left running here would record bounds nothing applies (codex
  // r2 HIGH). Suspend, do not tear down: the visitor's mode and area
  // (`mapSearchActive`, `mapBounds`) stay in state so rotating back to the
  // list layout restores exactly what they had (codex fresh pass) — the
  // renderers seed their camera from those bounds on the sheet. While
  // suspended the UI is hidden and every gesture/moveend handler is inert.
  const suspended = mobileResults.active
  const suspendedRef = useRef(suspended)
  suspendedRef.current = suspended
  const searchOnMove = options.map?.searchOnMove !== false
  // Tri-state default (SBP-10, #929): the new searchOnMoveDefault key wins;
  // the legacy activateSearchOnFirstGesture boolean maps to 'gesture' only
  // while the new key is unset, so stored configs keep their behavior.
  const defaultMode = options.map?.searchOnMoveDefault
    ?? (options.map?.activateSearchOnFirstGesture === true ? 'gesture' : 'off')
  const firstGestureEnabled = searchOnMove && defaultMode === 'gesture'
  const defaultOnEnabled = searchOnMove && defaultMode === 'on'
  const searchAreaEnabled = searchOnMove && options.map?.searchAreaButton === true

  const searchActiveRef = useRef(state.mapSearchActive)
  useEffect(() => {
    searchActiveRef.current = state.mapSearchActive
  }, [state.mapSearchActive])

  const armedRef = useRef(firstGestureEnabled)
  const [armed, setArmed] = useState(firstGestureEnabled)
  const disarm = useCallback(() => {
    if (armedRef.current) {
      armedRef.current = false
      setArmed(false)
    }
  }, [])

  const gestureSeenRef = useRef(false)
  const [showSearchArea, setShowSearchArea] = useState(false)

  // Any activation path (including external ones) disarms and hides the pill.
  useEffect(() => {
    if (state.mapSearchActive) {
      disarm()
      setShowSearchArea(false)
    }
  }, [state.mapSearchActive, disarm])

  // Option reconciliation (fresh-pass finding): the playground live-updates
  // options on a mounted widget, so arming/pill state must follow the switches.
  // Disabling first-gesture disarms (revealing the checkbox); re-enabling it
  // deliberately re-arms, but only while search is inactive — never yank an
  // active search back to armed.
  useEffect(() => {
    if (!firstGestureEnabled) {
      disarm()
    } else if (!armedRef.current && !searchActiveRef.current) {
      armedRef.current = true
      setArmed(true)
    }
  }, [firstGestureEnabled, disarm])

  useEffect(() => {
    if (!searchAreaEnabled) {
      setShowSearchArea(false)
    }
  }, [searchAreaEnabled])

  // Default-ON ('on' has no module equivalent — the module only gesture-arms):
  // the checkbox starts checked; bounds attach on the visitor's first real
  // move, so the initial fit stays unfiltered. Activation-only, mirroring the
  // gesture re-arm rule: flipping the option away (or a visitor unchecking)
  // never yanks or restores state by itself.
  useEffect(() => {
    if (defaultOnEnabled && !searchActiveRef.current) {
      searchActiveRef.current = true
      setMapSearchActive(true)
    }
  }, [defaultOnEnabled, setMapSearchActive])

  // Disabling search-on-move entirely hides the checkbox UI — but an already
  // ACTIVE search would keep filtering by invisible bounds (fresh-pass 2).
  // Clear the whole machine synchronously with the option change.
  useEffect(() => {
    if (!searchOnMove) {
      disarm()
      gestureSeenRef.current = false
      setShowSearchArea(false)
      if (searchActiveRef.current) {
        searchActiveRef.current = false
        setMapSearchActive(false)
        setMapBounds(null)
      }
    }
  }, [searchOnMove, disarm, setMapSearchActive, setMapBounds])

  const onUserGesture = useCallback(() => {
    if (suspendedRef.current) return
    gestureSeenRef.current = true
    if (armedRef.current) {
      armedRef.current = false
      setArmed(false)
      // Set the ref synchronously: this gesture's own debounced moveend must
      // already see search as active, ahead of React's state flush.
      searchActiveRef.current = true
      setMapSearchActive(true)
    }
  }, [setMapSearchActive])

  const onMoveEnd = useCallback((bounds: MapBoundsBox | null) => {
    if (suspendedRef.current) return
    // The gesture token is CONSUMED at the top of EVERY moveend (including
    // search-active ones): the pill may only appear on the moveend directly
    // following a genuine gesture. Leaving the token set on the active path
    // would let a later programmatic fitBounds resurrect the pill after the
    // visitor unchecks search-as-move.
    const gestured = gestureSeenRef.current
    gestureSeenRef.current = false
    if (searchActiveRef.current) {
      if (bounds) setMapBounds(bounds)
      return
    }
    if (searchAreaEnabled && gestured && !armedRef.current) {
      setShowSearchArea(true)
    }
  }, [searchAreaEnabled, setMapBounds])

  const onSearchAreaClick = useCallback((bounds: MapBoundsBox | null) => {
    if (suspendedRef.current) return
    setShowSearchArea(false)
    searchActiveRef.current = true
    setMapSearchActive(true)
    setMapBounds(bounds)
  }, [setMapSearchActive, setMapBounds])

  const onToggle = useCallback((checked: boolean, bounds: MapBoundsBox | null) => {
    if (suspendedRef.current) return
    disarm()
    gestureSeenRef.current = false
    setShowSearchArea(false)
    searchActiveRef.current = checked
    setMapSearchActive(checked)
    setMapBounds(checked ? bounds : null)
  }, [disarm, setMapSearchActive, setMapBounds])

  return {
    // UI gate: hidden while suspended; the state underneath is untouched.
    searchOnMove: searchOnMove && !suspended,
    searchActive: state.mapSearchActive,
    searchActiveRef,
    showSearchArea,
    concealSearchToggle: armed,
    onUserGesture,
    onMoveEnd,
    onSearchAreaClick,
    onToggle,
  }
}

export type MapSearchControls = ReturnType<typeof useMapSearchControls>
